import { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';

export function useSecurity() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  const updatePassword = async (currentPassword: string, newPassword: string) => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });
      
      if (error) throw error;
      
      toast.success('Password updated successfully');
      return true;
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Failed to update password');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const setupMFA = async () => {
    try {
      setLoading(true);
      const code = Math.random().toString(36).substring(2, 8).toUpperCase();
      
      const { error: storeError } = await supabase
        .from('mfa_codes')
        .insert({
          user_id: user?.id,
          code,
          expires_at: new Date(Date.now() + 10 * 60 * 1000).toISOString()
        });

      if (storeError) throw storeError;

      const response = await fetch(`${supabase.supabaseUrl}/functions/v1/send-notification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${supabase.supabaseKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: '2fa',
          userId: user?.id,
          subject: 'Your 2FA Verification Code',
          content: code
        })
      });

      if (!response.ok) throw new Error('Failed to send verification email');

      toast.success('Verification code sent to your email');
      return true;
    } catch (error) {
      console.error('Error setting up MFA:', error);
      toast.error('Failed to setup MFA');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const verifyMFA = async (code: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('mfa_codes')
        .select('*')
        .eq('user_id', user?.id)
        .eq('code', code)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        throw new Error('Invalid or expired code');
      }

      const { error: enableError } = await supabase
        .from('user_mfa')
        .upsert({
          user_id: user?.id,
          enabled: true,
          updated_at: new Date().toISOString()
        });

      if (enableError) throw enableError;

      await supabase
        .from('mfa_codes')
        .delete()
        .eq('user_id', user?.id);

      toast.success('2FA enabled successfully!');
      return true;
    } catch (error) {
      console.error('Error verifying MFA:', error);
      toast.error('Invalid verification code');
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    updatePassword,
    setupMFA,
    verifyMFA
  };
}